import { useCallback, useEffect, useRef, useState } from 'react';

import {
  PLANNING_REDONE_MESSAGE,
  PLANNING_UNDONE_MESSAGE,
} from 'constants/messageConstants';
import {
  PLANNING_UNDO_HISTORY_LIMIT,
  PLANNING_UNDO_NOTE_DURATION_MS,
} from 'constants/uiConstants';
import type {
  FarmExclusionState,
  FarmFormValues,
  PaddockFormValues,
} from 'utils/farmPayloads';

import {
  placeUndoNote,
  type PlanningUndoNote,
} from '../usePlanningUndo';

/** One change in the Paddocks grid that undo can step back over. */
export type PaddocksUndoEntry =
  | {
      kind: 'farmField';
      label: string;
      farmId: string;
      before: Partial<FarmFormValues>;
      after: Partial<FarmFormValues>;
    }
  | {
      kind: 'paddockField';
      label: string;
      farmId: string;
      paddockId: string;
      before: Partial<PaddockFormValues>;
      after: Partial<PaddockFormValues>;
    }
  | {
      kind: 'exclusion';
      label: string;
      before: ReadonlyMap<string, FarmExclusionState>;
      after: ReadonlyMap<string, FarmExclusionState>;
    };

interface UsePaddocksUndoArgs {
  /** The view's root, so the keys only act while focus is in the lens. */
  containerRef: React.RefObject<HTMLElement>;
  stageFarm: (farmId: string, patch: Partial<FarmFormValues>) => void;
  stagePaddock: (
    farmId: string,
    paddockId: string,
    patch: Partial<PaddockFormValues>
  ) => void;
  /** Saves each farm's recorded exclusion state. */
  restoreExclusion: (states: ReadonlyMap<string, FarmExclusionState>) => void;
}

// A dialog, popover or open picker list holds its own edits.
const OWNS_UNDO_SELECTOR = '.ant-modal, .ant-popover, .ant-select-dropdown';

/**
 * Keyboard undo in the Paddocks grid, like Planning's (OP-2539, OP-2481):
 * Ctrl/⌘+Z undoes the last field edit or Exclude / Include made here in this
 * visit, and Ctrl/⌘+Shift+Z redoes it, with the same note beside the focus.
 * Grid fields are nearly always focused, so the keys only fall to the field
 * while it holds unsaved typing (data-dirty). Field undos go through the
 * normal autosave; exclusions are saved straight away. Adding and deleting
 * aren't undone — a delete can't be brought back from here.
 */
export const usePaddocksUndo = ({
  containerRef,
  stageFarm,
  stagePaddock,
  restoreExclusion,
}: UsePaddocksUndoArgs) => {
  const pastRef = useRef<PaddocksUndoEntry[]>([]);
  const futureRef = useRef<PaddocksUndoEntry[]>([]);
  const [note, setNote] = useState<PlanningUndoNote | null>(null);
  const noteTimerRef = useRef<number | null>(null);

  const record = useCallback((entry: PaddocksUndoEntry) => {
    pastRef.current = [...pastRef.current, entry].slice(
      -PLANNING_UNDO_HISTORY_LIMIT
    );
    futureRef.current = [];
  }, []);

  const argsRef = useRef({ stageFarm, stagePaddock, restoreExclusion });
  argsRef.current = { stageFarm, stagePaddock, restoreExclusion };

  const step = useCallback((direction: 'undo' | 'redo') => {
    const from = direction === 'undo' ? pastRef : futureRef;
    const to = direction === 'undo' ? futureRef : pastRef;
    const entry = from.current[from.current.length - 1];
    if (!entry) return false;
    from.current = from.current.slice(0, -1);
    to.current = [...to.current, entry];

    const target = direction === 'undo' ? 'before' : 'after';
    const args = argsRef.current;
    if (entry.kind === 'farmField') {
      args.stageFarm(entry.farmId, entry[target]);
    } else if (entry.kind === 'paddockField') {
      args.stagePaddock(entry.farmId, entry.paddockId, entry[target]);
    } else {
      args.restoreExclusion(entry[target]);
    }

    const focused = document.activeElement;
    const rect =
      focused instanceof HTMLElement && focused !== document.body
        ? focused.getBoundingClientRect()
        : null;
    if (noteTimerRef.current !== null) {
      window.clearTimeout(noteTimerRef.current);
    }
    setNote({
      text:
        direction === 'undo'
          ? PLANNING_UNDONE_MESSAGE(entry.label)
          : PLANNING_REDONE_MESSAGE(entry.label),
      position:
        rect && (rect.width || rect.height)
          ? placeUndoNote(rect, window.innerWidth)
          : null,
    });
    noteTimerRef.current = window.setTimeout(
      () => setNote(null),
      PLANNING_UNDO_NOTE_DURATION_MS
    );
    return true;
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.key.toLowerCase() !== 'z'
      ) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest(OWNS_UNDO_SELECTOR)) return;
      if (
        target !== document.body &&
        !containerRef.current?.contains(target)
      ) {
        return;
      }
      if (target.matches('input, textarea')) {
        const field = target.closest('[data-grid-col]');
        // Unsaved typing, or a field outside the grid (search): its own undo.
        if (!field || field.getAttribute('data-dirty') === 'true') return;
      }
      if (step(event.shiftKey ? 'redo' : 'undo')) event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [containerRef, step]);

  useEffect(
    () => () => {
      if (noteTimerRef.current !== null) {
        window.clearTimeout(noteTimerRef.current);
      }
    },
    []
  );

  return { record, note };
};

export default usePaddocksUndo;
